import React, { useState, useEffect } from 'react';
import { usuariosAPI, niveisAcessoAPI } from '../services/api';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import UserBadge from '../components/UserBadge';
import { Plus, Edit, Trash2, Search, Filter } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useNivelAcesso } from '../hooks/useNivelAcesso';
import { useConfirm } from '../hooks/useConfirm';
import { PageSkeleton, SkeletonTabela } from '../components/Skeleton';

const Users = () => {
  const showConfirm = useConfirm();
  const { 
    user, 
    isAdmin, 
    isGerente, 
    podeGerenciarNivel, 
    getNiveisDisponiveis,
    podeEditarUsuario 
  } = useNivelAcesso();
  const { darkMode } = useTheme();
  const [users, setUsers] = useState([]);
  const [niveis, setNiveis] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroNivel, setFiltroNivel] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('active');
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    senha: '',
    nivel_acesso: 'usuario'
  });

  useEffect(() => {
    loadUsers();
    loadNiveis();
  }, []);

  const loadUsers = async () => {
    try {
      // Usar endpoint específico para usuários da mesma conta
      const response = await usuariosAPI.listMinhaConta();
      setUsers(response.data.usuarios || []);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const loadNiveis = async () => {
    try {
      const response = await niveisAcessoAPI.getDescricoes();
      setNiveis(response.data);
    } catch (error) {
      console.error('Erro ao carregar níveis:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const userData = {
        ...formData,
        cliente_id: user?.cliente_id
      };
      
      // Validar se pode criar usuário com este nível
      if (!editingUser && formData.nivel_acesso && !podeGerenciarNivel(formData.nivel_acesso)) {
        toast.error('Você não tem permissão para criar usuário com este nível de acesso');
        return;
      }
      
      if (editingUser) {
        await usuariosAPI.update(editingUser.id, userData);
        toast.success('Usuário atualizado com sucesso');
      } else {
        await usuariosAPI.create(userData);
        toast.success('Usuário criado com sucesso');
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({ nome: '', email: '', telefone: '', senha: '', nivel_acesso: 'usuario' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar usuário');
    }
  };

  const handleEdit = (userItem) => {
    setEditingUser(userItem);
    setFormData({
      nome: userItem.nome,
      email: userItem.email,
      telefone: userItem.telefone,
      senha: '',
      nivel_acesso: userItem.nivel_acesso || 'usuario'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    showConfirm({
      title: "Deletar Usuário",
      message: "Tem certeza que deseja deletar este usuário?",
      confirmText: "Deletar",
      type: "danger",
      onConfirm: async () => {
        try {
          await usuariosAPI.delete(id);
          toast.success('Usuário deletado com sucesso');
          loadUsers();
        } catch (error) {
          toast.error('Erro ao deletar usuário');
        }
      }
    });
  };

  const handleChangeNivel = async (userId, novoNivel) => {
    try {
      await usuariosAPI.alterarNivelAcesso({ usuario_id: userId, nivel_acesso: novoNivel });
      toast.success('Nível de acesso alterado com sucesso');
      loadUsers();
    } catch (error) {
      toast.error('Erro ao alterar nível de acesso');
    }
  };



  const filteredUsers = users.filter(u => {
    // Esconder admins da listagem pra quem não é admin
    if (!isAdmin() && u.nivel_acesso === 'admin') return false;
    if (filtroStatus === 'active' && !u.status) return false;
    if (filtroStatus === 'inactive' && u.status) return false;
    if (filtroNivel && u.nivel_acesso !== filtroNivel) return false;
    if (searchTerm) {
      const busca = searchTerm.toLowerCase();
      if (!u.nome?.toLowerCase().includes(busca) && !u.email?.toLowerCase().includes(busca)) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <Layout>
        <PageSkeleton title="Gerenciar Usuários" subtitle="Gerencie os usuários da sua equipe">
          <SkeletonTabela colunas={5} linhas={6} />
        </PageSkeleton>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader title="Gerenciar Usuários" subtitle="Gerencie os usuários da sua equipe">
          {(isAdmin() || isGerente()) && (
            <button
              onClick={() => setShowModal(true)}
              className="gradient-button inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md text-white"
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Novo Usuário</span>
            </button>
          )}
        </PageHeader>

        {/* Filtros e totais */}
        <div className={`p-4 rounded-lg border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex flex-wrap items-center gap-3">
            <Filter className={`w-5 h-5 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
            
            {/* Busca */}
            <div className="relative flex-1 min-w-[180px]">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
              />
            </div>

            {/* Nível */}
            <select
              value={filtroNivel}
              onChange={(e) => setFiltroNivel(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value="">Todos níveis</option>
              <option value="gerente">Gerente</option>
              <option value="operador">Operador</option>
              <option value="usuario">Usuário</option>
              <option value="visualizador">Visualizador</option>
            </select>

            {/* Status */}
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value="active">Ativos</option>
              <option value="all">Todos</option>
              <option value="inactive">Inativos</option>
            </select>

            {/* Total */}
            <div className={`px-3 py-2 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-gray-100'} text-center min-w-[60px]`}>
              <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{filteredUsers.length}</p>
              <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>usuários</p>
            </div>
          </div>
        </div>

        <div className={`overflow-x-auto overflow-hidden shadow rounded-lg ${
          darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={darkMode ? 'bg-zinc-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  darkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>Nome</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  darkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>Email</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  darkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>Telefone</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  darkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>Nível</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  darkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>Status</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  darkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>Ações</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'bg-zinc-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              {filteredUsers.map((userItem) => (
                <tr key={userItem.id} className={darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-50'}>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>{userItem.nome}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}>{userItem.email}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}>{userItem.telefone}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <UserBadge nivel={userItem.nivel_acesso || 'usuario'} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      userItem.status 
                        ? darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-800'
                        : darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-800'
                    }`}>
                      {userItem.status ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {podeEditarUsuario(userItem) && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(userItem)}
                          className={`p-2 rounded-md transition-colors ${
                            darkMode 
                              ? 'text-blue-400 hover:text-blue-300 hover:bg-zinc-700' 
                              : 'text-indigo-600 hover:text-indigo-900 hover:bg-gray-100'
                          }`}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {isGerente() && (
                          <>
                            {isAdmin() && (
                              <select
                                value={userItem.nivel_acesso || 'usuario'}
                                onChange={(e) => handleChangeNivel(userItem.id, e.target.value)}
                                className={`text-xs border rounded px-2 py-1 ${
                                  darkMode
                                    ? 'border-gray-600 bg-zinc-700 text-white'
                                    : 'border-gray-300 bg-white text-gray-900'
                                }`}
                              >
                                {getNiveisDisponiveis().map(({ value, label }) => (
                                  <option key={value} value={value}>{label}</option>
                                ))}
                              </select>
                            )}
                            <button
                              onClick={() => handleDelete(userItem.id)}
                              className="p-2 rounded-md text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto backdrop-blur-sm">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setShowModal(false)}></div>
              
              <div className={`relative rounded-2xl shadow-2xl transform transition-all max-w-lg w-full ${
                darkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-white'
              }`}>
                {/* Header */}
                <div className={`px-6 py-5 border-b ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {editingUser ? '✏️ Editar Usuário' : '➕ Novo Usuário'}
                    </h3>
                    <button
                      onClick={() => {
                        setShowModal(false);
                        setEditingUser(null);
                        setFormData({ nome: '', email: '', telefone: '', senha: '', nivel_acesso: 'usuario' });
                      }}
                      className={`rounded-lg p-2 transition-colors ${
                        darkMode ? 'hover:bg-zinc-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4" autoComplete="off">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Nome Completo
                    </label>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      autoComplete="off"
                      className={`w-full px-4 py-2.5 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-nomoz-navy ${
                        darkMode
                          ? 'border-zinc-600 bg-zinc-700 text-white placeholder-gray-400'
                          : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                      }`}
                      placeholder="João Silva"
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      autoComplete="off"
                      className={`w-full px-4 py-2.5 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-nomoz-navy ${
                        darkMode
                          ? 'border-zinc-600 bg-zinc-700 text-white placeholder-gray-400'
                          : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                      }`}
                      placeholder="joao@exemplo.com"
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Telefone
                    </label>
                    <input
                      type="text"
                      value={formData.telefone}
                      onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                      autoComplete="off"
                      className={`w-full px-4 py-2.5 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-nomoz-navy ${
                        darkMode
                          ? 'border-zinc-600 bg-zinc-700 text-white placeholder-gray-400'
                          : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                      }`}
                      placeholder="(11) 99999-9999"
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Senha {!editingUser && <span className="text-nomoz-orange">(opcional)</span>}
                    </label>
                    <input
                      type="password"
                      value={formData.senha}
                      onChange={(e) => setFormData({...formData, senha: e.target.value})}
                      autoComplete="new-password"
                      className={`w-full px-4 py-2.5 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-nomoz-navy ${
                        darkMode
                          ? 'border-zinc-600 bg-zinc-700 text-white placeholder-gray-400'
                          : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                      }`}
                      placeholder={editingUser ? "Deixe vazio para manter" : "Deixe vazio para gerar automática"}
                    />
                    {!editingUser && (
                      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Se vazio, será enviada senha temporária por e-mail
                      </p>
                    )}
                  </div>

                  {(isAdmin() || isGerente()) && (
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Nível de Acesso
                      </label>
                      <select
                        value={formData.nivel_acesso}
                        onChange={(e) => setFormData({...formData, nivel_acesso: e.target.value})}
                        className={`w-full px-4 py-2.5 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-nomoz-navy ${
                          darkMode
                            ? 'border-zinc-600 bg-zinc-700 text-white'
                            : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      >
                        {getNiveisDisponiveis().map(({ value, label }) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>

                      {/* Permissões do nível selecionado */}
                      {formData.nivel_acesso && (
                        <div className={`mt-3 p-3 rounded-lg text-xs ${darkMode ? 'bg-zinc-900 border border-zinc-700' : 'bg-gray-50 border border-gray-200'}`}>
                          <p className={`font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            Este perfil pode:
                          </p>
                          <ul className={`space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {{
                              gerente: [
                                '✅ Criar, editar e executar campanhas',
                                '✅ Criar e gerenciar templates',
                                '✅ Atendimento (conversas, IA, fluxos)',
                                '✅ Gerenciar usuários (criar, excluir)',
                                '✅ Configurações e financeiro',
                                '✅ Exportar dados (CSV)',
                              ],
                              operador: [
                                '✅ Criar, editar e executar campanhas',
                                '✅ Criar e gerenciar templates',
                                '✅ Atendimento (conversas, IA, fluxos)',
                                '✅ Exportar dados (CSV)',
                                '❌ Não gerencia financeiro',
                                '❌ Não exclui usuários',
                              ],
                              usuario: [
                                '✅ Visualizar campanhas',
                                '✅ Atendimento (conversas, IA, fluxos)',
                                '✅ Analytics e relatórios',
                                '❌ Não cria/edita campanhas',
                                '❌ Não exporta dados',
                                '❌ Não gerencia usuários',
                              ],
                              visualizador: [
                                '✅ Dashboard e analytics (leitura)',
                                '✅ Grupos WhatsApp (filtrar)',
                                '❌ Não envia mensagens',
                                '❌ Não cria campanhas',
                                '❌ Não exporta dados',
                                '❌ Sem acesso a financeiro ou configurações',
                              ],
                            }[formData.nivel_acesso]?.map((perm, i) => (
                              <li key={i}>{perm}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingUser(null);
                        setFormData({ nome: '', email: '', telefone: '', senha: '', nivel_acesso: 'usuario' });
                      }}
                      className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                        darkMode
                          ? 'bg-zinc-700 text-white hover:bg-zinc-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 transition-all shadow-lg hover:shadow-xl"
                    >
                      {editingUser ? '✓ Atualizar' : '✓ Criar Usuário'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Users;